<!--
  A segmented control, as V1 used for its statistics ranges.

  A real radio group rather than a row of buttons, so arrow keys move between the options
  and a screen reader announces "2 of 4 selected". The active segment is filled with the
  accent, and is the one place in the panel where a filled accent surface means
  "this is the current view" rather than "this is a primary action".
-->
<script setup lang="ts">
export interface SegmentOption {
  value: string;
  label: string;
  /** Announced instead of the label where the label alone is ambiguous. */
  description?: string;
}

defineProps<{
  options: SegmentOption[];
  modelValue: string;
  label: string;
}>();

const emit = defineEmits<{ (event: 'update:modelValue', value: string): void }>();

function select(value: string): void {
  emit('update:modelValue', value);
}

/** Arrow keys move the selection, which is what a radio group is expected to do. */
function onKeydown(event: KeyboardEvent, options: SegmentOption[], current: string): void {
  const step =
    event.key === 'ArrowRight' || event.key === 'ArrowDown'
      ? 1
      : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
        ? -1
        : 0;
  if (step === 0) return;

  event.preventDefault();
  const index = options.findIndex((option) => option.value === current);
  const next = options[(index + step + options.length) % options.length];
  if (next) select(next.value);
}
</script>

<template>
  <div class="shio-segmented" role="radiogroup" :aria-label="label">
    <button
      v-for="option in options"
      :key="option.value"
      type="button"
      role="radio"
      class="shio-segmented__option"
      :class="{ 'shio-segmented__option--active': option.value === modelValue }"
      :aria-checked="option.value === modelValue"
      :tabindex="option.value === modelValue ? 0 : -1"
      :title="option.description"
      @click="select(option.value)"
      @keydown="onKeydown($event, options, modelValue)"
    >
      {{ option.label }}
    </button>
  </div>
</template>

<style scoped>
.shio-segmented {
  display: inline-flex;
  gap: 2px;
  padding: 2px;
  border: 1px solid hsl(var(--shio-border));
  border-radius: var(--shio-radius-md);
  background: hsl(var(--shio-surface-base));
}

.shio-segmented__option {
  padding: var(--shio-space-1) var(--shio-space-3);
  border: 0;
  border-radius: calc(var(--shio-radius-md) - 3px);
  background: transparent;
  color: hsl(var(--shio-text-secondary));
  font-family: var(--shio-font-body);
  font-size: var(--shio-type-body-sm-size);
  cursor: pointer;
}

.shio-segmented__option:hover {
  color: hsl(var(--shio-text-primary));
}

.shio-segmented__option:focus-visible {
  outline: 2px solid hsl(var(--shio-accent-cyan));
  outline-offset: 1px;
}

.shio-segmented__option--active {
  background: hsl(var(--shio-accent-primary));
  /* Near-black on the accent, which clears AA where white on it does not. */
  color: hsl(var(--shio-background));
  font-weight: 600;
}
</style>
